/**
 * Citation Intelligence Analysis Utilities
 *
 * Calculates aggregated metrics from citation classification data
 * for display in the AI Visibility Report.
 */

import { isOwnDomainCitation } from "@repo/core/client";
import { extractDomain } from "@shared/lib/citation-gaps";

// Gap derivation now lives in @shared/lib/citation-gaps so the earned-media
// drill-down and this report name the same categories. Re-exported here for
// the report's existing importers.
export {
	extractDomain,
	getCompetitorCitations,
	groupCitationsByField,
	identifyCitationGaps,
} from "@shared/lib/citation-gaps";
export type {
	CitationGap,
	CitationGapStats,
	CompetitorCitation,
} from "@shared/lib/citation-gaps";

export interface CitationWithIntelligence {
	id: string;
	url: string;
	title?: string | null;
	description?: string | null;
	isOurDomain: boolean;
	brandAttributed?: string | null;
	// Citation Intelligence fields
	pageType?: string | null;
	contentFormat?: string | null;
	mediaType?: string | null;
	authorityType?: string | null;
	isListicle?: boolean | null;
	urlDepth?: number | null;
	classificationMethod?: string | null;
	classificationConfidence?: number | null;
	classifiedAt?: string | null;
}

export interface MediaTypeBreakdown {
	owned: number;
	earnedPress: number;
	earnedMention: number;
	reviewPlatform: number;
	socialCommunity: number;
	reference: number;
	videoPlatform: number;
	thirdParty: number;
	unclassified: number;
	[key: string]: number; // Index signature for dynamic access
}

export interface AuthorityTypeBreakdown {
	academic: number;
	government: number;
	majorMedia: number;
	tradeMedia: number;
	analyst: number;
	professionalOrg: number;
	reviewAggregator: number;
	encyclopedia: number;
	company: number;
	unclassified: number;
	[key: string]: number; // Index signature for dynamic access
}

export interface ContentFormatBreakdown {
	listicle: number;
	howToGuide: number;
	review: number;
	researchData: number;
	caseStudy: number;
	video: number;
	podcast: number;
	news: number;
	reference: number;
	standard: number;
	unclassified: number;
	[key: string]: number; // Index signature for dynamic access
}

export interface PageTypeBreakdown {
	homepage: number;
	blogArticle: number;
	productPage: number;
	servicePage: number;
	pricingPage: number;
	aboutPage: number;
	documentation: number;
	resourcePage: number;
	landingPage: number;
	other: number;
	unclassified: number;
	[key: string]: number; // Index signature for dynamic access
}

/**
 * Detailed citation info for display in citation lists
 */
export interface CitationDetail {
	url: string;
	domain: string;
	title: string | null;
	description: string | null;
	isOurDomain: boolean;
	brandAttributed: string | null;
	// Classification data
	mediaType: string | null;
	authorityType: string | null;
	contentFormat: string | null;
	pageType: string | null;
	isListicle: boolean | null;
	// Query context
	queryText: string;
	queryType: string;
	platform: string;
}

export interface CitationIntelligenceStats {
	totalCitations: number;
	classifiedCitations: number;
	classificationRate: number;

	// Brand vs third-party breakdown
	ownedCitations: number;
	thirdPartyCitations: number;

	// Listicle analysis
	listicleCitations: number;
	listicleRate: number;

	// Media type breakdown
	mediaTypes: MediaTypeBreakdown;

	// Authority type breakdown
	authorityTypes: AuthorityTypeBreakdown;

	// Content format breakdown
	contentFormats: ContentFormatBreakdown;

	// Page type breakdown
	pageTypes: PageTypeBreakdown;

	// Classification confidence
	avgConfidence: number;
	highConfidenceCount: number;

	// Key insights
	topMediaType: string;
	topAuthorityType: string;
	topContentFormat: string;

	// Detailed citation lists for brand analysis
	ownedMediaCitations: CitationDetail[]; // Citations from brand's own domain (isOurDomain = true)
	brandMentionCitations: CitationDetail[]; // Citations from third-party sites that mention the brand (brandAttributed = brand name, isOurDomain = false)
	competitorOwnedCitations: CitationDetail[]; // Citations from competitor domains (for comparison when no third-party brand mentions)
}

/**
 * Extract all citations from discovery results
 */
export function extractCitationsFromDiscoveryResults(
	discoveryResults: any[],
): CitationWithIntelligence[] {
	const citations: CitationWithIntelligence[] = [];

	for (const execution of discoveryResults) {
		for (const platformExecution of execution.platformExecutions || []) {
			for (const citation of platformExecution.citations || []) {
				citations.push(citation);
			}
		}
	}

	return citations;
}

/**
 * Extract citations with query context for detailed analysis
 */
export function extractCitationsWithContext(discoveryResults: any[]): Array<
	CitationWithIntelligence & {
		queryText: string;
		queryType: string;
		platform: string;
	}
> {
	const citations: Array<
		CitationWithIntelligence & {
			queryText: string;
			queryType: string;
			platform: string;
		}
	> = [];

	for (const execution of discoveryResults) {
		const queryText =
			execution.propertyQuery?.query ||
			execution.querySnapshot?.query ||
			"";
		const queryType =
			execution.propertyQuery?.queryType ||
			execution.querySnapshot?.queryType ||
			"";

		for (const platformExecution of execution.platformExecutions || []) {
			const platform =
				platformExecution.platform ||
				platformExecution.platformName ||
				"";

			for (const citation of platformExecution.citations || []) {
				citations.push({
					...citation,
					queryText,
					queryType,
					platform,
				});
			}
		}
	}

	return citations;
}

/**
 * Get all citations with full details - meant to be called lazily when user expands
 * the "All Citations" section to avoid processing upfront
 */
export function getAllCitationsWithDetails(
	discoveryResults: any[],
	brandName: string | undefined,
	// The subject's domain, or null when it owns no website (PERSON / CATEGORY).
	// Required so `isOurDomain` can't be trusted for a domainless subject, whose
	// legacy rows have it set true on every citation.
	brandDomain: string | null,
): {
	allCitations: CitationDetail[];
	brandCitations: CitationDetail[];
	thirdPartyCitations: CitationDetail[];
	/** @deprecated Use thirdPartyCitations instead */
	competitorCitations: CitationDetail[];
	/** @deprecated Merged into thirdPartyCitations */
	unattributedCitations: CitationDetail[];
} {
	const citationsWithContext = extractCitationsWithContext(discoveryResults);

	const allCitations: CitationDetail[] = [];
	const brandCitations: CitationDetail[] = [];
	const competitorCitations: CitationDetail[] = [];

	// Track seen URLs to deduplicate
	const seenUrls = new Set<string>();

	for (const citation of citationsWithContext) {
		// Skip duplicates
		if (seenUrls.has(citation.url)) {
			continue;
		}
		seenUrls.add(citation.url);

		// Normalize ownership once so the stored flag and every categorization
		// below agree — and so a domainless subject can't own any source.
		const isOwnDomain = isOwnDomainCitation(
			citation.isOurDomain,
			brandDomain,
		);
		const citationDetail: CitationDetail = {
			url: citation.url,
			domain: extractDomain(citation.url),
			title: citation.title || null,
			description: citation.description || null,
			isOurDomain: isOwnDomain,
			brandAttributed: citation.brandAttributed || null,
			mediaType: citation.mediaType || null,
			authorityType: citation.authorityType || null,
			contentFormat: citation.contentFormat || null,
			pageType: citation.pageType || null,
			isListicle: citation.isListicle || null,
			queryText: citation.queryText,
			queryType: citation.queryType,
			platform: citation.platform,
		};

		allCitations.push(citationDetail);

		// Categorize by attribution
		// Brand citations: from our domain OR third-party mentioning our brand
		const isThirdPartyBrandMention =
			!isOwnDomain &&
			citation.brandAttributed &&
			brandName &&
			citation.brandAttributed
				.toLowerCase()
				.includes(brandName.toLowerCase());

		if (isOwnDomain || isThirdPartyBrandMention) {
			brandCitations.push(citationDetail);
		} else {
			// All non-brand citations go to "third-party"
			competitorCitations.push(citationDetail);
		}
	}

	return {
		allCitations,
		brandCitations,
		thirdPartyCitations: competitorCitations,
		competitorCitations,
		unattributedCitations: [],
	};
}

/**
 * Calculate citation intelligence statistics from discovery results
 */
export function calculateCitationIntelligenceStats(
	discoveryResults: any[],
	brandName: string | undefined,
	// The subject's domain, or null when it owns no website — see
	// getAllCitationsWithDetails.
	brandDomain: string | null,
): CitationIntelligenceStats {
	const citations = extractCitationsFromDiscoveryResults(discoveryResults);
	const citationsWithContext = extractCitationsWithContext(discoveryResults);

	const totalCitations = citations.length;
	const classifiedCitations = citations.filter((c) => c.classifiedAt).length;

	// Build citation detail lists for brand analysis
	// (all citations are loaded lazily via separate function for "Explore All" section)
	const ownedMediaCitations: CitationDetail[] = [];
	const brandMentionCitations: CitationDetail[] = [];
	const competitorOwnedCitations: CitationDetail[] = [];

	// Track seen URLs to deduplicate within each list
	const seenOwnedUrls = new Set<string>();
	const seenBrandUrls = new Set<string>();
	const seenCompetitorUrls = new Set<string>();

	for (const citation of citationsWithContext) {
		const isOwnDomain = isOwnDomainCitation(
			citation.isOurDomain,
			brandDomain,
		);
		const citationDetail: CitationDetail = {
			url: citation.url,
			domain: extractDomain(citation.url),
			title: citation.title || null,
			description: citation.description || null,
			isOurDomain: isOwnDomain,
			brandAttributed: citation.brandAttributed || null,
			mediaType: citation.mediaType || null,
			authorityType: citation.authorityType || null,
			contentFormat: citation.contentFormat || null,
			pageType: citation.pageType || null,
			isListicle: citation.isListicle || null,
			queryText: citation.queryText,
			queryType: citation.queryType,
			platform: citation.platform,
		};

		// Collect owned media citations (isOurDomain = true)
		if (isOwnDomain && !seenOwnedUrls.has(citation.url)) {
			seenOwnedUrls.add(citation.url);
			ownedMediaCitations.push(citationDetail);
		}

		// Collect THIRD-PARTY brand mention citations
		// These are citations from sites we DON'T own that mention our brand
		// (brandAttributed matches brand BUT isOurDomain is false)
		const isThirdPartyBrandMention =
			!isOwnDomain &&
			citation.brandAttributed &&
			brandName &&
			citation.brandAttributed
				.toLowerCase()
				.includes(brandName.toLowerCase());

		if (isThirdPartyBrandMention && !seenBrandUrls.has(citation.url)) {
			seenBrandUrls.add(citation.url);
			brandMentionCitations.push(citationDetail);
		}

		// Collect competitor owned media citations (mediaType = 'owned' but not our domain)
		// These are citations from competitor websites, useful for comparison
		const isCompetitorOwned =
			!isOwnDomain && citation.mediaType === "owned";

		if (isCompetitorOwned && !seenCompetitorUrls.has(citation.url)) {
			seenCompetitorUrls.add(citation.url);
			competitorOwnedCitations.push(citationDetail);
		}
	}

	// Initialize breakdowns
	const mediaTypes: MediaTypeBreakdown = {
		owned: 0,
		earnedPress: 0,
		earnedMention: 0,
		reviewPlatform: 0,
		socialCommunity: 0,
		reference: 0,
		videoPlatform: 0,
		thirdParty: 0,
		unclassified: 0,
	};

	const authorityTypes: AuthorityTypeBreakdown = {
		academic: 0,
		government: 0,
		majorMedia: 0,
		tradeMedia: 0,
		analyst: 0,
		professionalOrg: 0,
		reviewAggregator: 0,
		encyclopedia: 0,
		company: 0,
		unclassified: 0,
	};

	const contentFormats: ContentFormatBreakdown = {
		listicle: 0,
		howToGuide: 0,
		review: 0,
		researchData: 0,
		caseStudy: 0,
		video: 0,
		podcast: 0,
		news: 0,
		reference: 0,
		standard: 0,
		unclassified: 0,
	};

	const pageTypes: PageTypeBreakdown = {
		homepage: 0,
		blogArticle: 0,
		productPage: 0,
		servicePage: 0,
		pricingPage: 0,
		aboutPage: 0,
		documentation: 0,
		resourcePage: 0,
		landingPage: 0,
		other: 0,
		unclassified: 0,
	};

	let ownedCitations = 0;
	let thirdPartyCitations = 0;
	let listicleCitations = 0;
	let totalConfidence = 0;
	let highConfidenceCount = 0;

	for (const citation of citations) {
		// Media type
		const mediaType = citation.mediaType;
		if (mediaType) {
			switch (mediaType) {
				case "owned":
					mediaTypes.owned++;
					ownedCitations++;
					break;
				case "earned_press":
					mediaTypes.earnedPress++;
					thirdPartyCitations++;
					break;
				case "earned_mention":
					mediaTypes.earnedMention++;
					thirdPartyCitations++;
					break;
				case "review_platform":
					mediaTypes.reviewPlatform++;
					thirdPartyCitations++;
					break;
				case "social_community":
					mediaTypes.socialCommunity++;
					thirdPartyCitations++;
					break;
				case "reference":
					mediaTypes.reference++;
					thirdPartyCitations++;
					break;
				case "video_platform":
					mediaTypes.videoPlatform++;
					thirdPartyCitations++;
					break;
				case "third_party":
					mediaTypes.thirdParty++;
					thirdPartyCitations++;
					break;
				default:
					mediaTypes.unclassified++;
					thirdPartyCitations++;
			}
		} else {
			mediaTypes.unclassified++;
			thirdPartyCitations++;
		}

		// Authority type
		const authorityType = citation.authorityType;
		if (authorityType) {
			switch (authorityType) {
				case "academic":
					authorityTypes.academic++;
					break;
				case "government":
					authorityTypes.government++;
					break;
				case "major_media":
					authorityTypes.majorMedia++;
					break;
				case "trade_media":
					authorityTypes.tradeMedia++;
					break;
				case "analyst":
					authorityTypes.analyst++;
					break;
				case "professional_org":
					authorityTypes.professionalOrg++;
					break;
				case "review_aggregator":
					authorityTypes.reviewAggregator++;
					break;
				case "encyclopedia":
					authorityTypes.encyclopedia++;
					break;
				case "company":
					authorityTypes.company++;
					break;
				default:
					authorityTypes.unclassified++;
			}
		} else {
			authorityTypes.unclassified++;
		}

		// Content format
		const contentFormat = citation.contentFormat;
		if (contentFormat) {
			switch (contentFormat) {
				case "listicle":
					contentFormats.listicle++;
					break;
				case "how_to_guide":
					contentFormats.howToGuide++;
					break;
				case "review":
					contentFormats.review++;
					break;
				case "research_data":
					contentFormats.researchData++;
					break;
				case "case_study":
					contentFormats.caseStudy++;
					break;
				case "video":
					contentFormats.video++;
					break;
				case "podcast":
					contentFormats.podcast++;
					break;
				case "news":
					contentFormats.news++;
					break;
				case "reference":
					contentFormats.reference++;
					break;
				case "standard":
					contentFormats.standard++;
					break;
				default:
					contentFormats.unclassified++;
			}
		} else {
			contentFormats.unclassified++;
		}

		// Page type
		const pageType = citation.pageType;
		if (pageType) {
			switch (pageType) {
				case "homepage":
					pageTypes.homepage++;
					break;
				case "blog_article":
					pageTypes.blogArticle++;
					break;
				case "product_page":
					pageTypes.productPage++;
					break;
				case "service_page":
					pageTypes.servicePage++;
					break;
				case "pricing_page":
					pageTypes.pricingPage++;
					break;
				case "about_page":
					pageTypes.aboutPage++;
					break;
				case "documentation":
					pageTypes.documentation++;
					break;
				case "resource_page":
					pageTypes.resourcePage++;
					break;
				case "landing_page":
					pageTypes.landingPage++;
					break;
				case "other":
					pageTypes.other++;
					break;
				default:
					pageTypes.unclassified++;
			}
		} else {
			pageTypes.unclassified++;
		}

		// Listicle detection
		if (citation.isListicle) {
			listicleCitations++;
		}

		// Confidence tracking
		if (
			citation.classificationConfidence !== null &&
			citation.classificationConfidence !== undefined
		) {
			totalConfidence += citation.classificationConfidence;
			if (citation.classificationConfidence >= 0.8) {
				highConfidenceCount++;
			}
		}
	}

	// Find top types
	const topMediaType = findTopKey(mediaTypes, ["unclassified"]);
	const topAuthorityType = findTopKey(authorityTypes, [
		"unclassified",
		"company",
	]);
	const topContentFormat = findTopKey(contentFormats, [
		"unclassified",
		"standard",
	]);

	return {
		totalCitations,
		classifiedCitations,
		classificationRate:
			totalCitations > 0
				? (classifiedCitations / totalCitations) * 100
				: 0,
		ownedCitations,
		thirdPartyCitations,
		listicleCitations,
		listicleRate:
			totalCitations > 0 ? (listicleCitations / totalCitations) * 100 : 0,
		mediaTypes,
		authorityTypes,
		contentFormats,
		pageTypes,
		avgConfidence:
			classifiedCitations > 0 ? totalConfidence / classifiedCitations : 0,
		highConfidenceCount,
		topMediaType,
		topAuthorityType,
		topContentFormat,
		// Detailed citation lists (brand's own only - all citations loaded lazily)
		ownedMediaCitations,
		brandMentionCitations,
		competitorOwnedCitations,
	};
}

/**
 * Find the key with highest value, excluding specified keys
 */
function findTopKey(obj: Record<string, number>, exclude: string[]): string {
	let topKey = "";
	let topValue = 0;

	for (const [key, value] of Object.entries(obj)) {
		if (!exclude.includes(key) && value > topValue) {
			topKey = key;
			topValue = value;
		}
	}

	return topKey || "unknown";
}

/**
 * Format media type for display
 */
export function formatMediaType(type: string): string {
	const labels: Record<string, string> = {
		owned: "Owned Media",
		earnedPress: "Press Coverage",
		earnedMention: "Web Articles & Blogs",
		reviewPlatform: "Review Sites",
		socialCommunity: "Social & Community",
		reference: "Reference Sites",
		videoPlatform: "Video Platforms",
		thirdParty: "Unclassified Source",
		unclassified: "Unclassified",
		// Snake case versions
		earned_press: "Press Coverage",
		earned_mention: "Web Articles & Blogs",
		review_platform: "Review Sites",
		social_community: "Social & Community",
		video_platform: "Video Platforms",
		third_party: "Unclassified Source",
	};
	return labels[type] || type;
}

/**
 * Format authority type for display
 */
export function formatAuthorityType(type: string): string {
	const labels: Record<string, string> = {
		academic: "Academic",
		government: "Government",
		majorMedia: "Major Media",
		tradeMedia: "Trade Media",
		analyst: "Industry Analysts",
		professionalOrg: "Professional Orgs",
		reviewAggregator: "Review Aggregators",
		encyclopedia: "Reference/Encyclopedia",
		company: "Other Brand Websites",
		unclassified: "Unclassified",
		// Snake case versions
		major_media: "Major Media",
		trade_media: "Trade Media",
		professional_org: "Professional Orgs",
		review_aggregator: "Review Aggregators",
	};
	return labels[type] || type;
}

/**
 * Format content format for display
 */
export function formatContentFormat(type: string): string {
	const labels: Record<string, string> = {
		listicle: "Listicles & Rankings",
		howToGuide: "How-To Guides",
		review: "Reviews",
		researchData: "Research & Data",
		caseStudy: "Case Studies",
		video: "Videos",
		podcast: "Podcasts",
		news: "News Articles",
		reference: "Reference Content",
		standard: "Other Articles & Pages",
		unclassified: "Unclassified",
		// Snake case versions
		how_to_guide: "How-To Guides",
		research_data: "Research & Data",
		case_study: "Case Studies",
	};
	return labels[type] || type;
}

/**
 * Get color for media type
 */
export function getMediaTypeColor(type: string): string {
	const colors: Record<string, string> = {
		owned: "bg-blue-100 text-blue-800",
		earnedPress: "bg-green-100 text-green-800",
		earnedMention: "bg-teal-100 text-teal-800",
		reviewPlatform: "bg-purple-100 text-purple-800",
		socialCommunity: "bg-pink-100 text-pink-800",
		reference: "bg-gray-100 text-gray-800",
		videoPlatform: "bg-red-100 text-red-800",
		thirdParty: "bg-orange-100 text-orange-800",
		unclassified: "bg-gray-100 text-gray-600",
	};
	return colors[type] || "bg-gray-100 text-gray-800";
}

/**
 * Get color for authority type
 */
export function getAuthorityTypeColor(type: string): string {
	const colors: Record<string, string> = {
		academic: "bg-indigo-100 text-indigo-800",
		government: "bg-blue-100 text-blue-800",
		majorMedia: "bg-green-100 text-green-800",
		tradeMedia: "bg-teal-100 text-teal-800",
		analyst: "bg-purple-100 text-purple-800",
		professionalOrg: "bg-cyan-100 text-cyan-800",
		reviewAggregator: "bg-orange-100 text-orange-800",
		encyclopedia: "bg-gray-100 text-gray-800",
		company: "bg-yellow-100 text-yellow-800",
		unclassified: "bg-gray-100 text-gray-600",
	};
	return colors[type] || "bg-gray-100 text-gray-800";
}
